import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';

const riskColor = (r: string) => r === 'critical' ? 'bg-red-100 text-red-700 border-red-200' : r === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-amber-100 text-amber-700 border-amber-200';


export default function SupAIInsightsPage() {
  const [aiInsights, setAIInsights] = useState<any[]>([]);
  const navigate = useNavigate();
  const [selectedInsight, setSelectedInsight] = useState<typeof aiInsights[0] | null>(null);

  const getRisk = (c: any) => {
    let score = 75;

    switch (c.incident_type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;
      case 'Physical Abuse':
        score = 90;
        break;
      case 'Emotional Abuse':
        score = 80;
        break;
      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;
      default:
        score = 70;
    }

    if (c.prior_incidents === 'yes') score += 5;
    if (c.priority_level === 'urgent') score += 3;
    if (c.priority_level === 'emergency') score += 5;

    if (score > 100) score = 100;

    let level = 'low';
    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { score, level };
  };

  useEffect(() => {
    fetch("https://guardiancloudcare.com/backend/api/get_ai_insights.php")
      .then(res => res.json())
      .then(data => setAIInsights(data))
      .catch(err => console.error(err));
  }, []);

  return (
    <AppLayout title="Case Insights" breadcrumb={['Supervisor', 'Insights']}>
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-5 text-white flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0"><i className="ri-brain-line text-2xl" /></div>
          <div>
            <h3 className="font-bold text-lg">Case Insights (RAG)</h3>
            <p className="text-teal-100 text-sm">generated summaries, risk analysis, and suggested actions — no need to read the full case file</p>
          </div>
        </div>

        {aiInsights.map((insight) => {
          const risk = getRisk(insight);

          return (
            <div key={insight.caseNumber} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className={`px-5 py-3 flex items-center gap-3 ${risk.level === 'critical' ? 'bg-red-50 border-b border-red-100' : 'bg-orange-50 border-b border-orange-100'}`}>
                <i className="ri-brain-line text-teal-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">{insight.childName}</p>
                  <p className="text-xs font-mono text-gray-500">{insight.caseNumber}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColor(risk.level)}`}>{risk.level}</span>
                <button onClick={() => setSelectedInsight(selectedInsight?.caseNumber === insight.caseNumber ? null : insight)}
                  className="text-xs bg-teal-500 text-white px-3 py-1.5 rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className={selectedInsight?.caseNumber === insight.caseNumber ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                  {selectedInsight?.caseNumber === insight.caseNumber ? 'Collapse' : 'View Insights'}
                </button>
              </div>

              {selectedInsight?.caseNumber === insight.caseNumber && (
                <div className="px-5 py-5 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1"><i className="ri-file-text-line" />AI Case Summary</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-4 border border-gray-100">{insight.summary}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1"><i className="ri-alarm-warning-line text-red-500" />Key Risks Identified</p>
                      <ul className="space-y-1.5">
                        {insight.keyRisks.map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <i className="ri-error-warning-line text-red-400 mt-0.5 flex-shrink-0" />{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1"><i className="ri-lightbulb-line text-amber-500" />Suggested Actions</p>
                      <ul className="space-y-1.5">
                        {insight.suggestedActions.map((a, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0" />{a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1"><i className="ri-scales-3-line text-amber-500" />Legal Basis</p>
                    <div className="flex gap-2 flex-wrap">
                      {insight.legalBasis.map((l) => (
                        <span key={l} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-full font-medium">{l}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2 border-t border-gray-100">
                    <button onClick={() => navigate(`/child-profiles/${insight.caseNumber}`)}
                      className="text-sm border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-eye-line" />View Full Case
                    </button>
                    <button onClick={() => navigate('/sup-approvals')}
                      className="text-sm bg-teal-500 text-white px-4 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-checkbox-circle-line" />Go to Approvals
                    </button>
                  </div>
                </div>
              )}
            </div>
        )})}
      </div>
    </AppLayout>
  );
}
